import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Slider from 'react-slick';
import cx from 'classnames';
import { useActivateSafeFromUrlParam, useIsProActivated } from 'hooks';
import useSafe from 'hooks/use-safe';
import { AppCard } from 'pages/AppsPage/components';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAppsIframe, useStxPrice } from 'store';
import { Tab } from 'ui-kit';

import { APP_LIST, Application } from '../AppsPage/appList';

import { BitflowBanner } from './components/NewsSlide/BitflowBanner';
import { hiddenIcon } from './util/images';
import { PeriodTabKey, useDashboardPage } from './util/useDashboardPage';
import { CustomizedAxisTick, CustomTooltip, NewsSlide } from './components';

import s from './index.module.scss';

export const DashboardPage: React.FC = () => {
  useActivateSafeFromUrlParam();

  const history = useHistory();
  const { safe, totalBalance } = useSafe();
  const stxPrice = useStxPrice();

  const isActivatedPro = useIsProActivated();
  const { setIframeRun, setSidebarHidden, setSelectedApp } = useAppsIframe();

  const safeFiatBalance = `$${totalBalance.toFixed(2)}`;

  const {
    tabs,
    selectedTabKey,
    setSelectedTabKey,
    loadHistory,
    allBalanceHistory,
    isAssetLoading,
  } = useDashboardPage();

  const settings = {
    dots: true,
    infinite: true,
    speed: 750,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 7500,
  };

  useEffect(() => {
    if (safe.address && stxPrice !== undefined) {
      loadHistory(safe.address, selectedTabKey, stxPrice).then();
    }
  }, [safe.address, selectedTabKey, stxPrice]);

  const handleAppClick = (app: Application) => {
    if (isActivatedPro) {
      setSelectedApp(app.url);
      setIframeRun(true);
      setSidebarHidden(true);
      history.push(`/vault/${safe.address}/apps?iframe`);
    } else {
      history.push(`/vault/${safe.address}/pro`);
    }
  };

  return (
    <div className="flex w-full pl-20 sm:pl-0 h-screen mb-32">
      <div className="flex flex-col gap-32 w-full relative pr-20 sm:pr-32 py-16 overflow-y-scroll">
        <div className="flex w-full items-end justify-between">
          <div className="flex flex-col gap-8">
            <p className="text-m14 text-regular">Vault Total Value</p>
            <p className="text-[36px] leading-[48px] text-regular-fa">{safeFiatBalance}</p>
          </div>
          <div className="flex gap-8 pb-4 sm:pb-0 overflow-x-auto min-h-44 mx-[-20px] sm:mx-0 px-16 sm:px-0">
            {(Object.keys(tabs) as PeriodTabKey[]).map((tabKey) => {
              return (
                <Tab
                  id={tabKey}
                  key={tabKey}
                  isActive={tabKey === selectedTabKey}
                  onClick={() => setSelectedTabKey(tabKey)}
                >
                  {tabs[tabKey]}
                </Tab>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-16 sm:gap-32 w-full">
          <div className="flex flex-col flex-1 rounded-[16px] bg-black-1 relative">
            <div
              className={cx(
                s.chart1Wrapper,
                'w-full min-h-[320rem] h-[320rem] pt-12 px-24 pb-24',
                isAssetLoading && 'bg-white-10 blur-[2px] rounded-[16px]'
              )}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={allBalanceHistory || []}
                  margin={{ top: 20, right: 6, left: 2, bottom: 0 }}
                  className={s.customChart}
                >
                  <CartesianGrid
                    stroke="var(--color-white-10)"
                    strokeDasharray="8 8"
                    vertical={false}
                    horizontal
                  />
                  <XAxis dataKey="date" tickMargin={12} minTickGap={21} />
                  <YAxis
                    width={44}
                    scale="auto"
                    tick={(props) => <CustomizedAxisTick {...props} />}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#71DD5F"
                    dot={selectedTabKey === 'week'}
                  />
                  <Tooltip content={(props) => <CustomTooltip {...props} />} cursor={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {isAssetLoading && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 flex flex-col items-center">
                <img src="/loading-gif.gif" width={24} height={24} alt="loading" />
                <p className="text-m15 text-regular">Loading data</p>
              </div>
            )}
            {!isAssetLoading && allBalanceHistory?.length === 0 && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 flex flex-col items-center">
                <img src={hiddenIcon} width={24} height={24} alt="" />
                <p className="text-m15 text-regular text-center">No balance history available</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-16 relative">
          <h2 className="text-r20 text-white">News</h2>
          <div className="w-full sm:max-w-[calc(100vw-310rem)]">
            {/* TODO check why slider shows error here */}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <Slider {...settings} className="w-full">
              {/* <NewsSlide
                title="Earn 12.5% APY on sBTC now."
                description="Supply your sBTC in Zest Protocol now to earn up to 12.5% on your sBTC and extra Zest Points. Directly from your Asigna Multisig!"
                link="https://app.zestprotocol.com/assets"
                btnTitle="Lend Now"
                index={1}
                /> */}
              <NewsSlide
                title="USDCx now supported on Stacks Multisig"
                description="USDCx is a 1:1 USDC-backed stablecoin native to Stacks, issued by Circle via xReserve. It offers USDC on Stacks without third-party bridges, wrapped assets, or fragmented liquidity."
                link="https://bridge.stacks.co/"
                btnTitle="Learn more"
                index={3}
              />
              <BitflowBanner />
              <NewsSlide
                title="The sBTC cap has been removed"
                description="Stay up to date on Stacks socials to learn when the cap will be extended."
                link="https://x.com/stacks"
                btnTitle="Learn more"
                index={1}
              />
              <NewsSlide
                title="Asigna V2 Is Live on Stacks"
                description="Experience a reimagined design, comprehensive history dashboard, real-time prices, enhanced user experience, a streamlined signing flow, and more exciting features coming soon!"
                link="https://twitter.com/asignaio"
                index={2}
              />
            </Slider>
          </div>
        </div>
        <div className="flex flex-col gap-24 mb-16">
          <div className="flex w-full items-center justify-between">
            <h2 className="text-r20 text-white">Trending Apps</h2>
            <button
              className="flex items-center gap-8 text-primary hover:text-[#B1A9FF] group"
              onClick={() => history.push(`/vault/${safe.address}/apps`)}
            >
              <p className="text-m14">Explore all</p>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="text-primary group-hover:text-[#B1A9FF]"
              >
                <path d="M5 13L10.5 7.5L5 2" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 flex-wrap gap-16 lg:gap-32 mb-32 sm:mb-0">
            {APP_LIST.slice(0, 4)
              .filter((item) => item.caption !== 'Velar')
              .map((item, index) => (
                <AppCard
                  key={item.url}
                  app={item}
                  isDashboard
                  index={index}
                  onClick={handleAppClick}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
